import styled from '@emotion/styled';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Pagination, Checkbox, Spin, Tag, Button } from 'antd';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import SafeImage from 'components/SafeImage';
import Icon, {
  ArrowsAltOutlined,
  DownOutlined,
  LinkOutlined,
  ShrinkOutlined,
  UpOutlined,
} from '@ant-design/icons';
import Empty from 'components/Empty';
import Highlighter from 'react-highlight-words';
import { ReactComponent as SimilarSvg } from '@knowledge/assets/similar.svg';
import { useListData } from './useListData';
import { useCurrentPageCheckboxState, useKeyword, useLabelQuery, usePage } from './state';
import { useStoreInfo } from './useStoreInfo';
import { useToggleSkuChecked, useToggleSpuChecked, useToggleCurrentPageChecked } from './state';
import useSellerSkuSelect from './SellerSkuSelect';
import { ConfigContext } from './context';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';
import toLowerCase from 'utils/toLowerCase';

function ItemList({
  skuDisabled,
  defaultFoldSku,
}: {
  skuDisabled?: boolean;
  defaultFoldSku?: boolean;
}) {
  const { t } = useTranslate();
  const [pageNo, pageSize, setPageNo] = usePage();

  const { pageInfo = { totalCount: 0 }, listData, loading } = useListData();
  const checkboxStatus = useCurrentPageCheckboxState();
  const toggleCurrentPageChecked = useToggleCurrentPageChecked();

  const [skuFold, setSkuFold] = useState(defaultFoldSku);

  return (
    <ItemListContainer>
      <div className="content">
        {loading ? (
          <div className="spin-container">
            <Spin />
          </div>
        ) : listData?.length > 0 ? (
          <ListView>
            <div className="page-select-all">
              <Checkbox
                onChange={(e) => toggleCurrentPageChecked(e.target.checked)}
                indeterminate={checkboxStatus === 'indeterminate'}
                checked={checkboxStatus === 'checked'}
              >
                {t('voucher.本页全选')}
              </Checkbox>
              <Button
                type="text"
                icon={skuFold ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                onClick={() => setSkuFold((fold) => !fold)}
              >
                {skuFold ? t('single_unfold_sku') : t('single_fold_sku')}
              </Button>
            </div>
            <div className="list-scroll-view">
              {listData.map((item) => (
                <Item
                  key={item.internalItemId}
                  item={item}
                  skuDisabled={skuDisabled}
                  skuFold={skuFold}
                />
              ))}
            </div>
          </ListView>
        ) : (
          <div className="spin-container">
            <Empty />
          </div>
        )}
      </div>
      <div className="pagination">
        <Pagination
          showSizeChanger
          size="small"
          total={pageInfo.totalCount}
          pageSize={pageSize}
          current={pageNo}
          onChange={(page, pageSize) => {
            setPageNo(page, pageSize);
          }}
        />
      </div>
    </ItemListContainer>
  );
}

type ItemProps = {
  item: ItemSelectV2.Item;
  mode?: 'item' | 'sellerSku';
  sellerSku?: string;
  skuDisabled?: boolean;
  skuFold?: boolean;
};

export function Item(props: ItemProps) {
  const { item, mode = 'item', sellerSku, skuDisabled, skuFold: skuFoldProp = false } = props;
  const [skuFold, setSkuFold] = useState(skuFoldProp);
  useEffect(() => {
    setSkuFold(skuFoldProp);
  }, [skuFoldProp]);
  const { t } = useTranslate();
  const selectedTagId = useLabelQuery();
  const { keyword, keywordType } = useKeyword();
  const { skuSelectDisabled, inSupplementKnowledge = false } = useContext(ConfigContext);
  const {
    platform,
    region,
    externalShopId,
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    internalItemId,
    skuInfo: _skuInfo = [],
    itemUrl,
    itemSku,
  } = item;
  const storeStr = useStoreInfo(item);
  const toggleSkuChecked = useToggleSkuChecked(mode);
  const toggleSpuChecked = useToggleSpuChecked(mode, sellerSku);
  const [open, sellerSkuSelect] = useSellerSkuSelect();
  const skuInfo = useMemo(() => {
    return mode === 'item'
      ? _skuInfo
      : _skuInfo.filter((sku) => sku.sellerSku.toLowerCase() === sellerSku?.toLowerCase());
  }, [mode, _skuInfo, sellerSku]);
  const xiapibuyEnable = useXiapibuyEnable(region);
  return (
    <div className={itemStyle}>
      <Checkbox
        onChange={(e) => toggleSpuChecked(e.target.checked, spuId)}
        indeterminate={item._indeterminate}
        checked={item._checked}
      />
      {sellerSkuSelect}
      <div className="basic-info">
        <div className="header">
          <Image hasSmallPic="both" src={imageUrl} width={60} />
          <div className="main-info">
            <div
              className="name"
              title={itemName}
              onClick={(e) => {
                e.stopPropagation();
                const link =
                  platform === 'SHOPEE' && xiapibuyEnable && !itemUrl.includes('.xiapibuy.com')
                    ? `https://${toLowerCase(
                        region,
                      )}.xiapibuy.com/product/${externalShopId}/${spuId}`
                    : itemUrl;
                window.open(link, '__blank');
              }}
            >
              {keywordType === 1 ? (
                <Highlighter autoEscape searchWords={[keyword || '']} textToHighlight={itemName} />
              ) : (
                itemName
              )}
            </div>
            <div className="link">
              <LinkOutlined />
            </div>
            <div className="sub-info">
              <div className="store-name">
                {t('marketing.店铺')}: {storeStr}
              </div>
              <Divider>|</Divider>
              <div className="spu-id">
                ID:{' '}
                {keywordType === 2 ? (
                  <Highlighter autoEscape searchWords={[keyword || '']} textToHighlight={spuId} />
                ) : (
                  spuId
                )}
              </div>
              <Divider>|</Divider>
              <div className="item-sku" title={itemSku}>
                {t('aide.主商品货号')}:{' '}
                {keywordType === 3 ? (
                  <Highlighter
                    autoEscape
                    searchWords={[keyword || '']}
                    textToHighlight={itemSku || '-'}
                  />
                ) : (
                  itemSku || '-'
                )}
              </div>
            </div>
            {!skuSelectDisabled && skuInfo.length > 0 && (
              <div className="sku-fold">
                <Button type="text" size="small" onClick={() => setSkuFold(!skuFold)}>
                  {t(`knowledge.共{{x}}个SKU${skuInfo.length > 1 ? '_other' : ''}`, {
                    x: skuInfo.length,
                  })}
                  {skuFold ? <DownOutlined /> : <UpOutlined />}
                </Button>
              </div>
            )}
          </div>
        </div>
        {!skuSelectDisabled && !skuFold && (
          <div className="sku-info">
            {skuInfo.map((sku) => {
              const renderTag = sku.tagSimpleData?.filter((tag) =>
                selectedTagId?.includes(tag.tagId),
              );

              const skuNode = (
                <div className="sku-name" title={`${sku.sellerSku}(${sku.skuName})`}>
                  <span>
                    {keywordType === 4 ? (
                      <Highlighter
                        autoEscape
                        searchWords={[keyword || '']}
                        textToHighlight={`${sku.sellerSku}(${sku.skuName})`}
                      />
                    ) : (
                      `${sku.sellerSku}(${sku.skuName})`
                    )}
                  </span>
                  {mode === 'item' && !inSupplementKnowledge && sku._checked && (
                    <TagList>
                      <Tag
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          open(sku.sellerSku, internalItemId);
                        }}
                        className="spec"
                        icon={<Icon component={SimilarSvg} style={{ color: '#336FFF' }} />}
                      >
                        {t('knowledge.item.sellerSku.same')}
                      </Tag>
                    </TagList>
                  )}
                  {mode === 'item' &&
                  !sku._checked &&
                  selectedTagId &&
                  selectedTagId?.length > 0 ? (
                    <TagList>
                      {renderTag?.length > 0 && (
                        <EllipsisTag color="geekblue">{renderTag[0].tagName}</EllipsisTag>
                      )}
                      {renderTag?.length > 1 && (
                        <Tag color="geekblue">
                          {t('knowledge.item.etc', { x: renderTag?.length - 1 })}
                        </Tag>
                      )}
                    </TagList>
                  ) : null}
                </div>
              );

              if (skuDisabled) {
                return (
                  <div className="sku" key={sku.skuId}>
                    {skuNode}
                  </div>
                );
              }

              return (
                <div className="sku" key={sku.skuId}>
                  <Checkbox
                    onChange={(e) => toggleSkuChecked(e.target.checked, spuId, sku.skuId)}
                    checked={sku._checked}
                  >
                    {skuNode}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const EllipsisTag = styled(Tag)`
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Image = styled(SafeImage)`
  width: 60px;
  height: 60px;
`;

export const Divider = styled.div`
  flex: 0 0 4.5%;
  text-align: center;
`;

const ItemListContainer = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  max-height: calc(100% - 52px);

  & > .content {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 48px);

    .spin-container {
      flex: 1 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  & > .pagination {
    border-top: 1px solid #f0f2f5;
    flex: 0 0 48px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0 16px;
  }
`;

const ListView = styled.div`
  height: 100%;
  padding-right: 9px;

  .page-select-all {
    display: flex;
    justify-content: space-between;
    height: 40px;
    line-height: 20px;
    padding: 11px 16px 0px 16px;
  }

  .list-scroll-view {
    height: calc(100% - 40px);
    padding: 0 13px 16px 16px;
    overflow: auto;
  }
`;

export const itemStyle = css`
  border: 1px solid #d3d7e0;
  border-radius: 2px;
  padding: 10px 18px 0px 8px;
  display: flex;
  gap: 8px;
  margin-bottom: 12px;

  .basic-info {
    width: calc(100% - 24px);

    .header {
      display: flex;
      gap: 8px;
      padding-bottom: 8px;
      cursor: pointer;

      .main-info {
        width: calc(100% - 68px);
        position: relative;
        overflow: hidden;
        & > .link {
          position: absolute;
          display: none;
          width: 20px;
          height: 20px;
          top: 0px;
          right: 0px;
          padding: 0 !important;
          line-height: 20px;
          text-align: center;
          background: #d6e8ff;
          color: #336fff;
          border-radius: 2px;
          cursor: pointer;
        }

        .name {
          flex-grow: 0;
          font-size: 14px;
          color: #232426;
          line-height: 20px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 4px;

          &:hover {
            color: var(--ant-primary-color);

            & + .link {
              display: block;
            }
          }
        }

        .sub-info {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #84868c;

          .store-name {
            flex: 0 1 auto;
            min-width: 120px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .spu-id {
            flex: 0 0 auto;
            white-space: nowrap;
          }

          .item-sku {
            flex: 0 1 auto;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }

        .sku-fold {
          display: flex;
          justify-content: flex-end;
          > button {
            padding-right: 0;
            font-size: 12px;
            color: #84868c;
          }
        }
      }
    }

    .sku-info {
      display: flex;
      flex-wrap: wrap;
      &:not(:empty) {
        border-top: 1px solid #f0f2f5;
        padding-top: 8px;
      }

      & > .sku {
        flex-grow: 0;
        flex-shrink: 0;
        flex-basis: calc(50% - 13px);
        max-width: calc(50% - 13px);
        overflow: hidden;
        display: flex;
        margin-bottom: 9px;

        .ant-checkbox-wrapper {
          width: 100%;

          & > span:nth-child(2) {
            overflow: hidden;

            .sku-name {
              color: #515359;
              font-size: 14px;
              display: flex;
              & > span {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }
          }
        }
      }
    }
  }
`;

export const TagList = styled.div`
  display: inline-flex;
  flex-grow: 1;
  margin-left: 4px;
  .ant-tag {
    background: #f0f7ff !important;
    color: #336fff !important;
    &.spec {
      border-color: rgba(214, 232, 255, 1);
      background: #fff !important;
    }
  }
`;

export default memo(ItemList);
